import { animated, useTransition } from '@react-spring/web';
import { cloneElement, memo, useCallback, useMemo, useRef, useState } from 'react';
import { differenceWith, isEqual } from 'es-toolkit';
import { getMessages } from '@/redux/modules/consoleMessages';
import { useAppSelector } from '@/redux/hooks';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import FormattedPreferredCurrency from '@/components/Formatted/FormattedPreferredCurrency';
import Messages, { MessageProps } from './helpers/Messages';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import useWindowSize from '@/hooks/useWindowSize';

type MessageData = {
    amount: number;
    bidderId: number;
    currency: string;
    limit: number;
    lotNumber: string;
    myBid: boolean;
    paddleNumber: string;
    previousBidAmount: number;
    serverCreatedTime: string;
    source: string;
};

type Message = {
    created: any;
    data: MessageData;
    messageId: any;
    text: string;
    type: string;
};

type Props = {
    catalogId: number;
    className?: string;
    convert?: boolean;
    hideMessages?: boolean;
    smallConsole?: boolean;
    zIndex?: number;
};

const MessageScroller = ({ catalogId, className, convert = false, hideMessages, smallConsole, zIndex }: Props) => {
    const messageList = useAppSelector((state) => getMessages(state, catalogId, smallConsole), isEqual) || [];
    const { height: windowHeight } = useWindowSize();
    const refMap = useMemo(() => new WeakMap(), []);
    const cancelMap = useMemo(() => new WeakMap(), []);
    const [items, setItems] = useState<Array<Message & { _uid: string }>>([]);
    const narrow = useIsNarrowerThan(1000);
    const uidCounter = useRef(0);

    // Generate truly unique key using assigned uid
    const getMessageKey = useCallback((item: Message & { _uid?: string }) => {
        return item._uid || `${item.created}-${item.type}`;
    }, []);

    const buildMessage = useCallback(
        ({ created, data = {} as MessageData, messageId, text, type }: Message) => {
            const {
                amount = 0,
                bidderId,
                currency = 'usd',
                limit = 0,
                lotNumber,
                myBid,
                paddleNumber,
                serverCreatedTime,
            } = data;

            const nonLaBidder = bidderId === 0;

            const whiteConvertedCurrencyText =
                ((type === 'accepted' || type === 'accepted2') && !nonLaBidder) ||
                type === 'opened' ||
                type === 'outbid' ||
                type === 'outbid-absentee' ||
                type === 'absentee-outbid' ||
                type === 'mybid' ||
                type === 'askChanged' ||
                type === 'sold';

            const values = {
                amount: (
                    <StyledCurrencyWrapper
                        key="message-scroller-amount"
                        noFraction
                        sourceCurrency={currency || 'usd'}
                        value={amount || 0}
                    />
                ),
                converted: convert ? (
                    <StyledConvertedCurrencyWrapper>
                        <StyledFormattedPreferredCurrency
                            colorWhite={whiteConvertedCurrencyText}
                            key="message-scroller-converted"
                            noBackground
                            noFraction
                            sourceCurrency={currency || 'usd'}
                            sourceValue={amount || 0}
                            wrapWithParens
                        />
                    </StyledConvertedCurrencyWrapper>
                ) : null,
                limit: (
                    <FormattedCurrency
                        key="message-scroller-limit"
                        noFraction
                        sourceCurrency={currency || 'usd'}
                        value={limit || 0}
                    />
                ),
                lotNumber,
                paddleNumber,
                serverCreatedTime,
            };

            const messageProps: MessageProps = {
                bidderId,
                created,
                hideMessage: false,
                limit,
                messageId,
                myBid,
                nonLaBidder,
                text,
                values,
            };

            // In case we get an unhandled type
            const MessageBuilder = Messages[type] || (() => null);

            return (
                <div>
                    <MessageBuilder {...messageProps} />
                    <MessageSpacer />
                </div>
            );
        },
        [convert]
    );

    const transitions = useTransition(items, {
        enter: (item) => async (next, cancel) => {
            cancelMap.set(item, cancel);
            await next({ height: refMap.get(item).offsetHeight, opacity: 1 });
        },
        from: { height: 0, opacity: 0 },
        keys: getMessageKey,
        // we only keep 80 messages max. this is as many as would show with a vertical 4k monitor
        onRest: () => setItems((state) => state.slice(-80)),
    });

    useDeepCompareEffect(() => {
        setItems((currentItems) => {
            // Compute new items inside callback to use actual current state (fixes React StrictMode double-render)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const currentItemsWithoutUid = currentItems.map(({ _uid: _, ...rest }) => rest);
            const actualNewItems = differenceWith(messageList, currentItemsWithoutUid, isEqual);
            if (actualNewItems.length === 0) {
                return currentItems;
            }
            // Assign unique IDs to new items to guarantee unique keys
            const itemsWithUid = actualNewItems.map((item) => ({
                ...item,
                _uid: `msg-${uidCounter.current++}`,
            }));
            return [...currentItems, ...itemsWithUid];
        });
    }, [messageList]);

    const height = narrow ? 124 : 150;

    return (
        <StyledMessageScroller
            $maxHeight={hideMessages ? height : windowHeight}
            $zIndex={zIndex}
            className={className}
            data-testid="console-message-scroller"
        >
            <StyledMessageList>
                {transitions(({ ...style }, item) => (
                    <StyledMessage
                        data-testid="message"
                        style={style}
                    >
                        {cloneElement(buildMessage(item), {
                            ref: (ref: HTMLDivElement) => ref && refMap.set(item, ref),
                        })}
                    </StyledMessage>
                ))}
            </StyledMessageList>
        </StyledMessageScroller>
    );
};

export default memo<Props>(MessageScroller);

const StyledMessageScroller = styled.div<{ $maxHeight: number; $zIndex?: number }>`
    && {
        overflow: hidden;
        max-width: 100%;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        user-select: none;
        pointer-events: none;
        touch-action: none;
        z-index: ${({ $zIndex }) => $zIndex || 'auto'};
    }
    &&& {
        max-height: ${({ $maxHeight }) => $maxHeight}px;
        min-height: ${({ $maxHeight }) => $maxHeight}px;
    }
`;

const StyledMessageList = styled.ul`
    overflow: hidden;
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;

const StyledCurrencyWrapper = styled(FormattedCurrency)`
    padding-left: 3px;
    color: ${({ theme }) => theme.colors.black} !important;
`;

const StyledConvertedCurrencyWrapper = styled.div`
    display: inline-flex;
    padding-right: 3px;
`;

const StyledFormattedPreferredCurrency = styled(FormattedPreferredCurrency).attrs(() => ({
    currencyClassName: 'currencyClass',
}))`
    .currencyClass {
        padding: 1px 0 0;
    }
`;

const StyledMessage = styled(animated.li)`
    position: relative;
    overflow: hidden;
    display: block;
    width: 100%;
`;

const MessageSpacer = styled.div`
    height: 8px;
`;
